// Docs: IMPLEMENTATION.md §3.4 (navigation components); FEATURES.md
// "Keyboard shortcuts". Studio shell (≥768px): fixed left rail replacing the
// old top bar. Top: goal-ring brand + the ⌘K entry. Middle: one button per
// route (heute carries today's open-lessons badge — lessons live there). Foot: profile + collapse
// toggle. Collapses to icons-only below 1024px automatically; a manual
// toggle wins and is persisted. Hidden entirely while a lesson runs
// (html[data-lesson-active], see index.css).
import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { userAPI, statsAPI, preferencesAPI } from '../services/api';
import { queryKeys } from '../queryKeys';
import { usePreferences } from '../hooks/useReferenceData';
import { calcStreak } from '../utils/calcStreak';
import { studioRail, studioProfileSections, isInSection } from '../utils/navItems';
import { useNextLesson } from '../hooks/useNextLesson';
import DailyGoalRing from './DailyGoalRing';
import NavIcon from './NavIcons';
import { trackEvent } from '../analytics';

const DEFAULT_DAILY_GOALS = { words: 30 };
const COLLAPSE_KEY = 'studioRailCollapsed';
const AUTO_COLLAPSE_QUERY = '(max-width: 1023px)';

const readStoredCollapse = () => {
  try {
    const v = localStorage.getItem(COLLAPSE_KEY);
    return v === null ? null : v === 'true';
  } catch {
    return null;
  }
};

const StudioRail = () => {
  const location = useLocation();
  const profileActive = studioProfileSections.some((section) => isInSection(location.pathname, section));
  const [manualCollapsed, setManualCollapsed] = useState(readStoredCollapse);
  const [autoCollapsed, setAutoCollapsed] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(AUTO_COLLAPSE_QUERY).matches
      : false,
  );

  useEffect(() => {
    const mq = window.matchMedia(AUTO_COLLAPSE_QUERY);
    const onChange = (e) => setAutoCollapsed(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const collapsed = manualCollapsed ?? autoCollapsed;

  // Main content offsets itself by this var (see .main--studio in index.css).
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--app-rail-width', collapsed ? '56px' : '176px');
    root.style.setProperty('--app-nav-height', '0px');
    return () => {
      root.style.setProperty('--app-rail-width', '0px');
    };
  }, [collapsed]);

  const toggleCollapse = () => {
    const next = !collapsed;
    setManualCollapsed(next);
    try {
      localStorage.setItem(COLLAPSE_KEY, String(next));
    } catch {
      /* ignore */
    }
  };

  // Goal ring / streak (same data the old top bar showed).
  const token =
    typeof window !== 'undefined' ? localStorage.getItem('jwt') : null;
  const { data: user } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: userAPI.getCurrentUser,
    enabled: !!token,
    staleTime: 5 * 60_000,
  });
  const { data: navStats } = useQuery({
    queryKey: queryKeys.stats.summary(90),
    queryFn: () => statsAPI.getStats(90),
    enabled: !!token,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const { data: prefsData } = usePreferences();
  const { data: dayWindow } = useQuery({
    queryKey: queryKeys.preferences.dayWindow,
    queryFn: preferencesAPI.getDayWindow,
    enabled: !!token,
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  });

  const dailyGoals = prefsData?.daily_goals || DEFAULT_DAILY_GOALS;
  const dailyStats = navStats?.daily_stats || [];
  const todayEntry = dayWindow?.today_local
    ? dailyStats.find((d) => d.date === dayWindow.today_local) || null
    : dailyStats[dailyStats.length - 1] || null;
  const streak = useMemo(
    () => calcStreak(dailyStats, dailyGoals, dayWindow?.today_local),
    [dailyStats, dailyGoals, dayWindow?.today_local],
  );

  const { openLessonCount } = useNextLesson();

  return (
    <nav
      className={`studio-rail hidden md:flex ${collapsed ? 'studio-rail--collapsed' : ''}`}
      aria-label="Hauptnavigation"
    >
      {/* Brand: goal ring → stats, wordmark → heute */}
      <div className="studio-rail__brand">
        <Link to="/stats" title="Tagesziel & Statistik" className="shrink-0">
          <DailyGoalRing
            dayStats={todayEntry}
            dailyGoals={dailyGoals}
            streak={streak}
            compact
          />
        </Link>
        {!collapsed && (
          <Link to="/" className="studio-rail__wordmark">
            deutsch trainer
          </Link>
        )}
      </div>

      {/* ⌘K */}
      <button
        type="button"
        className="studio-rail__kbtn"
        onClick={() => {
          trackEvent('command_palette_open', { via: 'rail_button' });
          window.dispatchEvent(new Event('palette:toggle'));
        }}
        title="Suchen & Befehle (⌘K)"
      >
        <NavIcon name="suche" size={15} />
        {!collapsed && (
          <>
            {/* Short label: "suchen & befehle" wrapped to two lines in the
                176px rail; the ⌘K hint and tooltip cover the commands. */}
            <span className="flex-1 text-left whitespace-nowrap truncate">suchen</span>
            <kbd>⌘K</kbd>
          </>
        )}
      </button>

      {/* Routes */}
      <div className="studio-rail__items">
        {studioRail.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            className={({ isActive }) =>
              `studio-rail__item ${isActive ? 'studio-rail__item--active' : ''}`
            }
            title={item.label}
            onClick={() => trackEvent('nav_tab_click', { tab: item.path, shell: 'studio' })}
          >
            <NavIcon name={item.icon} size={17} />
            {!collapsed && <span className="flex-1">{item.label}</span>}
            {!collapsed && item.path === '/' && openLessonCount > 0 && (
              <span className="studio-rail__badge">{openLessonCount}</span>
            )}
          </NavLink>
        ))}
      </div>

      {/* Foot */}
      <div className="studio-rail__foot">
        <NavLink
          to="/profile"
          className={() =>
            `studio-rail__item ${profileActive ? 'studio-rail__item--active' : ''}`
          }
          title="Profil & Einstellungen"
          onClick={() => trackEvent('nav_tab_click', { tab: '/profile', shell: 'studio' })}
        >
          <NavIcon name="profil" size={17} />
          {!collapsed && (
            <span className="flex-1 truncate">{user?.name || 'profil'}</span>
          )}
        </NavLink>
        <button
          type="button"
          className="studio-rail__item studio-rail__item--muted"
          onClick={toggleCollapse}
          title={collapsed ? 'Ausklappen' : 'Einklappen'}
        >
          <NavIcon name={collapsed ? 'ausklappen' : 'einklappen'} size={17} />
          {!collapsed && <span>einklappen</span>}
        </button>
      </div>
    </nav>
  );
};

export default StudioRail;
